import { useState } from 'react';

import { api, type JaratNap, type JaratSor } from '../api.ts';
import { useMuvelet } from '../felhasznalo-context.ts';
import { ft } from '../formazas.ts';

/**
 * Sofőrönkénti túrasorrend. (A megrendelő kérése, 2026-10-22.)
 *
 * A Járatszervezés **Összeírás** nézete változatlan: ott áll össze a nap
 * és ott történik a kiosztás. Ez a nézet utána jön, és egyetlen kérdésre
 * felel: a sofőr milyen sorrendben járja végig a saját címeit.
 *
 * Ezért itt csak az számít, ami ehhez kell — sorrend, étterem, cím,
 * mennyiség. A tizenöt oszlopos tábla mellett a sorrendezés elveszne.
 *
 * **A sorrend üresen hagyható.** Ahol nincs megadva, ott a nyomtatás a
 * nap közös sorrendje szerint megy, mint eddig — és nem öröklődik a
 * következő napra sem.
 */
export function TuraNezet({
  datum,
  jarat,
  soforok,
  tervezheto,
  onHiba,
  onUzenet,
  onFrissit,
}: {
  datum: string;
  jarat: JaratNap | null;
  soforok: Array<{ id: number; nev: string }>;
  tervezheto: boolean;
  onHiba: (h: string | null) => void;
  onUzenet: (u: string | null) => void;
  onFrissit: () => Promise<void>;
}) {
  /** A túrasorrend a diszpécser és az iroda dolga; a sofőr nem állítja. */
  const allithat = useMuvelet('tura_sorrend') || useMuvelet('jarat_torles');
  const [mentes, setMentes] = useState(false);

  const sorok = jarat?.sorok ?? [];
  const csoportok = [
    ...soforok.map((so) => ({
      sofor_id: so.id as number | null,
      nev: so.nev,
      sorok: sorok.filter((s) => s.sofor_id === so.id),
    })),
    { sofor_id: null as number | null, nev: 'Még nincs kiosztva', sorok: sorok.filter((s) => s.sofor_id === null) },
  ].filter((cs) => cs.sorok.length > 0);

  /** A csoport sorai úgy, ahogy a nyomtatott lapon állnának. */
  const turaszerint = (lista: JaratSor[]) => {
    if (!lista.some((s) => s.tura_sorrend != null)) return lista;
    return [...lista].sort(
      (a, b) => (a.tura_sorrend ?? 9999) - (b.tura_sorrend ?? 9999) || a.sorrend - b.sorrend,
    );
  };

  async function muvelet(mit: () => Promise<unknown>, uzenet?: string) {
    if (mentes) return;
    setMentes(true);
    onHiba(null);
    try {
      await mit();
      await onFrissit();
      if (uzenet) onUzenet(uzenet);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A művelet nem sikerült.');
    } finally {
      setMentes(false);
    }
  }

  const sorrendMentes = (id: number, ertek: string) =>
    muvelet(() =>
      api.post(`/api/jarat/tetel/${id}/tura-sorrend`, {
        ertek: ertek.trim() === '' ? null : Number(ertek),
      }),
    );

  /** Egy sor mozgatása: a szomszédjával cserél helyet. */
  async function mozgat(lista: JaratSor[], index: number, irany: -1 | 1) {
    const cel = index + irany;
    if (cel < 0 || cel >= lista.length) return;
    /* Ha még nincs sorrend, a mostani sorrend adja az alapszámokat. */
    const szamok = lista.map((s, i) => s.tura_sorrend ?? i + 1);
    const a = lista[index]!;
    const b = lista[cel]!;
    await muvelet(async () => {
      await api.post(`/api/jarat/tetel/${a.id}/tura-sorrend`, { ertek: szamok[cel] });
      await api.post(`/api/jarat/tetel/${b.id}/tura-sorrend`, { ertek: szamok[index] });
    });
  }

  if (sorok.length === 0) {
    return (
      <div className="uzenet flag" style={{ marginTop: 14 }}>
        Ezen a napon még nincs cím. Az <b>Összeírás</b> nézetben állítsd össze a napot, oszd ki a
        sofőrökre, és utána térj ide vissza a sorrendért.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      {!tervezheto && (
        <div className="uzenet flag" style={{ marginBottom: 10 }}>
          Ez a nap már ki van adva, a sorrend nem változtatható. Ami a papíron van, az marad.
        </div>
      )}

      {csoportok.map((cs) => {
        const lista = turaszerint(cs.sorok);
        const vanSorrend = lista.some((s) => s.tura_sorrend != null);
        const liter = lista.reduce((a, s) => a + (s.terv_liter ?? 0), 0);
        const szerkesztheto = allithat && tervezheto && cs.sofor_id !== null;

        return (
          <div key={cs.sofor_id ?? 'nincs'} className="turablokk">
            <div className="turafej">
              <b>{cs.nev}</b>
              <span className="c">
                {lista.length} cím · {ft(liter)} l
              </span>
              <span style={{ flex: 1 }} />
              {szerkesztheto && (
                <>
                  <button
                    className="btn sm"
                    disabled={!vanSorrend || mentes}
                    title="A lyukakat tünteti el: 2, 5, 7 → 1, 2, 3"
                    onClick={() =>
                      void muvelet(
                        () =>
                          api.post(`/api/jarat/${datum}/tura-sorrend/ujraszamozas`, {
                            sofor_id: cs.sofor_id,
                          }),
                        `${cs.nev}: a sorrend újraszámozva.`,
                      )
                    }
                  >
                    Újraszámozás
                  </button>
                  <button
                    className="btn sm"
                    disabled={!vanSorrend || mentes}
                    title="A sorrend törlése — a nyomtatás visszaáll a nap közös sorrendjére"
                    onClick={() =>
                      void muvelet(
                        () =>
                          api.post(`/api/jarat/${datum}/tura-sorrend/torles`, {
                            sofor_id: cs.sofor_id,
                          }),
                        `${cs.nev}: a sorrend törölve.`,
                      )
                    }
                  >
                    Sorrend törlése
                  </button>
                </>
              )}
            </div>

            {cs.sofor_id === null && (
              <div className="hint" style={{ padding: '0 0 6px' }}>
                Ezek a címek még nincsenek sofőrhöz rendelve — sorrendet sem kapnak. Az Összeírás
                nézetben oszd ki őket.
              </div>
            )}

            <table className="tt2 turatabla">
              <thead>
                <tr>
                  <th className="n" style={{ width: 70 }}>
                    Sorrend
                  </th>
                  <th style={{ width: 62 }} />
                  <th>Étterem</th>
                  <th>Cím</th>
                  <th className="n" style={{ width: 70 }}>
                    MO / FO
                  </th>
                  <th className="n" style={{ width: 80 }}>
                    Mennyiség
                  </th>
                </tr>
              </thead>
              <tbody>
                {lista.map((s, i) => (
                  <tr key={s.id} className={s.tura_sorrend == null ? 'nincssorrend' : undefined}>
                    <td className="c">
                      {szerkesztheto ? (
                        <input
                          className="turaszam"
                          type="number"
                          min={1}
                          max={999}
                          defaultValue={s.tura_sorrend ?? ''}
                          key={`${s.id}-${s.tura_sorrend ?? ''}`}
                          onBlur={(e) => {
                            const uj = e.target.value.trim();
                            const regi = s.tura_sorrend == null ? '' : String(s.tura_sorrend);
                            if (uj !== regi) void sorrendMentes(s.id, uj);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          }}
                        />
                      ) : (
                        <span className="calc2">{s.tura_sorrend ?? '—'}</span>
                      )}
                    </td>
                    <td className="c">
                      {szerkesztheto && (
                        <>
                          <button
                            className="xb"
                            disabled={i === 0 || mentes}
                            title="Feljebb"
                            onClick={() => void mozgat(lista, i, -1)}
                          >
                            ↑
                          </button>
                          <button
                            className="xb"
                            disabled={i === lista.length - 1 || mentes}
                            title="Lejjebb"
                            onClick={() => void mozgat(lista, i, 1)}
                          >
                            ↓
                          </button>
                        </>
                      )}
                    </td>
                    <td className="pn">{s.etterem_nev}</td>
                    <td className="c">
                      {[s.etterem_irsz, s.etterem_cim].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="calc2">
                      {[s.terv_mo ? `${ft(s.terv_mo)} MO` : '', s.terv_fo ? `${ft(s.terv_fo)} FO` : '']
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </td>
                    <td className="calc2">{s.terv_liter ? `${ft(s.terv_liter)} l` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {!allithat && (
        <div className="hint" style={{ marginTop: 8 }}>
          A sorrend beállításához külön engedély kell („Túrasorrend beállítása").
        </div>
      )}
    </div>
  );
}
